import type { IBankingChange } from './types'

const SOM_IN_BILLION = 1_000_000_000
const SOM_IN_MILLION = 1_000_000

/**
 * Numbers use Russian separators in both locales: Kyrgyz writes numbers the same way, while
 * `ky-KG` data differs between Node and browsers (comma vs point), which broke hydration.
 */
const NUMBER_LOCALE = 'ru-RU'

export const useBankingNumber = () => {
  const { t } = useI18n()

  const number = (value: number, maximumFractionDigits: number, minimumFractionDigits = 0) =>
    new Intl.NumberFormat(NUMBER_LOCALE, {
      minimumFractionDigits,
      maximumFractionDigits
    }).format(value)

  /** Soms are shown in billions (млрд) from 1 billion, otherwise in millions (млн). */
  const formatSom = (value: number): string => {
    if (Math.abs(value) >= SOM_IN_BILLION) {
      return `${number(value / SOM_IN_BILLION, 1, 1)} ${t('banking_statistics.unit_bn')}`
    }

    return `${number(value / SOM_IN_MILLION, 1, 1)} ${t('banking_statistics.unit_mn')}`
  }

  /** An axis tick in billions of soms without the unit, which the axis names once: "1 000". */
  const formatBillions = (value: number): string => number(value / SOM_IN_BILLION, 0)

  const formatPercent = (value: number): string => `${number(value, 2, 2)}%`

  /** A whole count, such as a number of banks: "1 234". */
  const formatCount = (value: number): string => number(value, 0)

  /** A plain number with exactly `fractionDigits` digits after the comma. */
  const formatNumber = (value: number, fractionDigits: number): string =>
    number(value, fractionDigits, fractionDigits)

  const formatChange = (change: IBankingChange): string => {
    const amount = number(Math.abs(change.value), 2)
    const unit = change.unit === 'pp' ? ` ${t('banking_statistics.unit_pp')}` : '%'
    const arrow = change.value > 0 ? '▲ ' : change.value < 0 ? '▼ ' : ''
    const suffix = change.basis === 'ytd' ? ` ${t('banking_statistics.since_year_start')}` : ''

    return `${arrow}${amount}${unit}${suffix}`
  }

  /** "2026-08-31" to "31.08.2026". */
  const formatDate = (iso: string): string => {
    const [year, month, day] = iso.slice(0, 10).split('-')

    return year && month && day ? `${day}.${month}.${year}` : iso
  }

  /** "2026-08-31" to "08.2026". */
  const formatMonth = (iso: string): string => {
    const [year, month] = iso.split('-')

    return year && month ? `${month}.${year}` : iso
  }

  return { formatSom, formatBillions, formatPercent, formatNumber, formatCount, formatChange, formatDate, formatMonth }
}
